'use client';

import { useEffect } from 'react';

// Stay visible while near the top of the page, so the header never flickers
// away on the first small downward nudge.
const HIDE_AFTER_PX = 96;
// Ignore sub-pixel jitter / momentum noise before committing to a direction.
const DELTA_PX = 6;

/**
 * Directional "headroom" behavior for the global site header.
 *
 * While mounted, this flips `data-nav-hidden` on the <html> element based on
 * scroll direction: scrolling down past the header hides it; any upward scroll
 * (or returning to the top) reveals it. The actual hide/reveal — and the
 * matching slide of the marketplace-intelligence anchor nav into the reclaimed
 * space — lives in CSS keyed off that attribute (see `globals.css`), so this
 * hook only owns the state.
 *
 * It also publishes the header's measured height as `--site-header-height` so
 * the anchor nav can travel exactly the distance the header slides. With the
 * two distances (and easing) matched, the header and the anchor nav move as one
 * rigid unit on reveal instead of drifting apart mid-transition.
 *
 * The behavior is scoped by mounting: only pages that call this hook ever set
 * the attribute, so the global CSS rules stay dormant everywhere else.
 */
export function useNavAutoHide() {
  useEffect(() => {
    const root = document.documentElement;

    // Keep `--site-header-height` in sync with the real header so the anchor
    // nav's travel distance always equals the header's slide (it re-measures on
    // resize / breakpoint changes / font load via ResizeObserver).
    const header = document.querySelector<HTMLElement>('.site-sticky-header');
    const measureHeader = () => {
      if (header) {
        root.style.setProperty('--site-header-height', `${header.offsetHeight}px`);
      }
    };
    measureHeader();
    const headerObserver = header ? new ResizeObserver(measureHeader) : null;
    if (header) {
      headerObserver?.observe(header);
    }

    let lastY = window.scrollY;
    let ticking = false;

    const setHidden = (hidden: boolean) => {
      if (hidden) {
        root.setAttribute('data-nav-hidden', '');
      } else {
        root.removeAttribute('data-nav-hidden');
      }
    };

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      if (y <= HIDE_AFTER_PX) {
        setHidden(false);
        lastY = y;
        return;
      }
      // Movement too small to judge direction — keep the current state and the
      // current reference point so slow scrolling still accumulates.
      if (Math.abs(y - lastY) < DELTA_PX) {
        return;
      }
      setHidden(y > lastY);
      lastY = y;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      headerObserver?.disconnect();
      root.removeAttribute('data-nav-hidden');
      root.style.removeProperty('--site-header-height');
    };
  }, []);
}
